// src/components/chart/ChartTooltip.tsx
import React from 'react';
import { Typography, Stack, Divider, Box, useTheme } from '@mui/material';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardRounded';
import { ChartTooltipFrame } from '../ChartCard';
import { formatCurrency, formatPairPrice, formatQuantity, formatSignedPercent, formatSignedPp, formatSignedUsd } from '../../../utils/formatters';

/** A point reads as cumulative P&L over today's capital: 1,000 ↔ 0%, 1,059.02 ↔ +5.90%. */
const formatIndex = (v: number) => formatSignedPercent(v / 1000 - 1);

/** Tighter line height for the compact (phone) readout. */
const DENSE_LINE_HEIGHT = 1.3;

/** Index row: value leads, the series is keyed by a short line. */
const IndexRow: React.FC<{ label: string; value: number; color: string; dense?: boolean }> = ({
  label,
  value,
  color,
  dense = false,
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 12, height: 2, borderRadius: 1, bgcolor: color }} />
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ...(dense && { lineHeight: DENSE_LINE_HEIGHT }) }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{
        fontSize: '0.875rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        ...(dense && { lineHeight: DENSE_LINE_HEIGHT }),
      }}
    >
      {formatIndex(value)}
    </Typography>
  </Stack>
);

const hasChange = (startValue?: number | null, endValue?: number | null, change?: number | null) =>
  change != null && !isNaN(change) && startValue != null && endValue != null;

/** Change detail row (Price / P&L) */
const ChangeDetailRow: React.FC<{
  label: string;
  startValue: number | null;
  endValue: number | null;
  change: number | null;
  valueFormatter: (val: number) => string;
  changeFormatter: (val: number) => string;
}> = ({ label, startValue, endValue, change, valueFormatter, changeFormatter }) => {
  if (!hasChange(startValue, endValue, change)) return null;
  const isPositive = change! >= 0;
  const color = isPositive ? 'success.main' : 'error.main';
  const Icon = isPositive ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Icon sx={{ fontSize: '0.8rem', color }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {changeFormatter(change!)}
          </Typography>
        </Stack>
      </Stack>
      <Typography
        sx={{ fontSize: '0.6875rem', color: 'text.secondary', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
      >
        {valueFormatter(startValue!)} → {valueFormatter(endValue!)}
      </Typography>
    </Box>
  );
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string | number;
  decimals?: number;
  /**
   * Phone readout: date, LP / HODL index and the gap vs HODL only (no price or P&L
   * detail), on a narrower frame.
   */
  compact?: boolean;
  /** Pair symbols, so prices read with their unit ("$2,648.31", "0.000231 WETH", sats). */
  pairTokens?: [string | undefined, string | undefined];
}

/**
 * Performance tooltip. The "today" point (plotted at the start of tomorrow)
 * is labelled "Now"; every other point shows its full date.
 */
const ChartTooltip: React.FC<ChartTooltipProps> = ({
  active,
  payload,
  label,
  decimals = 2,
  compact = false,
  pairTokens,
}) => {
  const theme = useTheme();

  if (!active || !payload || payload.length === 0) return null;
  // Si todos los valores vienen nulos, no renderizamos
  if (payload.every(p => p.value == null)) return null;

  // Points sit at UTC midnight: show that UTC calendar day in any timezone (a local format would
  // print the previous day west of UTC). The live point is plotted in the future → "Now".
  const dateTs = typeof label === 'number' ? label : Date.parse(label as string);
  const utc = new Date(dateTs);
  const dateObject = new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());

  // Lines are drawn from smoothed keys; the readout always uses the raw indexes.
  const poolIndexData = payload.find(p => p.dataKey === 'poolIndex' || p.dataKey === 'poolIndexSmooth');
  // The HODL line may be drawn from a smoothed key; the readout always uses the raw index.
  const tokenIndexData = payload.find(p => p.dataKey === 'tokenIndex' || p.dataKey === 'tokenIndexSmooth');
  const dataPoint = poolIndexData?.payload;
  if (!poolIndexData || !dataPoint) return null;

  const rawTokenIndex: number | undefined = tokenIndexData ? dataPoint.tokenIndex ?? tokenIndexData.value : undefined;
  const hodlIndex = rawTokenIndex != null && Number.isFinite(rawTokenIndex) ? rawTokenIndex : null;
  // Both indexes start at 1,000, so their gap / 1,000 is LP − HODL in percentage points.
  const rawPoolIndex: number = dataPoint.poolIndex ?? poolIndexData.value;
  const vsHodl = hodlIndex != null ? (rawPoolIndex - hodlIndex) / 1000 : null;
  const vsHodlColor = (vsHodl ?? 0) >= 0 ? theme.palette.success.main : theme.palette.error.main;

  const {
    previousPrice,
    currentPrice,
    previousPnl,
    currentPnl,
    priceChange,
    pnlChange
  } = dataPoint as {
    previousPrice: number | null;
    currentPrice: number | null;
    previousPnl: number | null;
    currentPnl: number | null;
    priceChange: number | null;
    pnlChange: number | null;
  };

  const formatPrice = (v: number) =>
    pairTokens ? formatPairPrice(v, pairTokens[0], pairTokens[1], decimals, 6) : formatQuantity(v, decimals);
  const formatUsd = (v: number) => formatCurrency(v, 0);

  const showDate = !(dateTs > Date.now());
  const showDetail =
    !compact &&
    (hasChange(previousPrice, currentPrice, priceChange) || hasChange(previousPnl, currentPnl, pnlChange));
  // The compact readout sits over the plot on phones: keep it short.
  const gap = compact ? 0.75 : 1.25;
  const labelSx = compact
    ? { fontSize: '0.75rem', fontWeight: 650, lineHeight: DENSE_LINE_HEIGHT }
    : { fontSize: '0.8125rem', fontWeight: 650 };

  return (
    <ChartTooltipFrame minWidth={compact ? 180 : 250}>
      <Typography sx={{ ...labelSx, mb: gap }}>
        {showDate ? format(dateObject, 'EEE, MMM d, yyyy') : 'Now'}
      </Typography>

      <Stack spacing={compact ? 0.5 : 0.75}>
        <IndexRow label="LP" value={rawPoolIndex} color={poolIndexData.stroke} dense={compact} />
        {hodlIndex != null && (
          <IndexRow label="HODL" value={hodlIndex} color={tokenIndexData.stroke} dense={compact} />
        )}
      </Stack>

      {showDetail && (
        <>
          <Divider sx={{ my: gap }} />
          <Stack spacing={0.75}>
            <ChangeDetailRow
              label="Pair price · day"
              startValue={previousPrice}
              endValue={currentPrice}
              change={priceChange}
              valueFormatter={formatPrice}
              changeFormatter={formatSignedPercent}
            />
            {/* Day change in USD; the line under it is the cumulative P&L before → after. */}
            <ChangeDetailRow
              label="Cumulative P&L · day"
              startValue={previousPnl}
              endValue={currentPnl}
              change={pnlChange != null && previousPnl != null && currentPnl != null ? currentPnl - previousPnl : null}
              valueFormatter={formatUsd}
              changeFormatter={formatSignedUsd}
            />
          </Stack>
        </>
      )}

      {vsHodl !== null && (
        <>
          <Divider sx={{ my: gap }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography sx={labelSx}>vs HODL</Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: vsHodlColor,
                fontVariantNumeric: 'tabular-nums',
                ...(compact && { lineHeight: DENSE_LINE_HEIGHT }),
              }}
            >
              {formatSignedPp(vsHodl)}
            </Typography>
          </Stack>
        </>
      )}
    </ChartTooltipFrame>
  );
};

export default ChartTooltip;
